/**
 * SecretModal Component
 *
 * Modal for viewing, creating, or editing organization secrets.
 * Supports provider-specific credentials with form validation.
 */

import { ApiError, stringifyLosslessJson } from '@pillar-portal/api-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Status } from '@pillar-portal/database/dist/frontend';
import { runInBackground } from '@/lib/utils';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useSecret, useSaveSecret } from '@/lib/hooks/useSecrets';
import {
    authApi,
    ipxCredentialManagementApi,
    organizationApi,
    rpaCredentialInjectionApi,
    sambaConfigApi,
    secretsApi,
} from '@/lib/pillarApi';
import type {
    ActiveRoleAssignmentResponse,
    CreateSecretResponse,
    CredentialPendingReason,
    CredentialTestRequest,
    CredentialTestResult,
    GetOrganizationResponse,
    IpxCredentialDefinitionProjection,
    IpxCredentialFieldDescriptor,
    IpxCredentialRuntimeBootstrapResponse,
    ListSecretCredentialDefinitionsResponse,
    MerchantConfig,
    SecretListItem,
    SecretMetadataResponse,
    SecretCredentialDefinitionListItem,
    SecretUuidReference,
    SupportedCredentialSystemId,
} from '@pillar-portal/api-types';
import {
    CredentialOwnerScope,
    CredentialTestRequestKind,
    CredentialTestStatus,
    IpxCredentialFieldClassification,
    MerchantConfigSchema,
    SupportedCredentialSystemIds,
    CredentialType,
    CredentialTypeDisplayName,
    GetOrganizationResponseSchema,
    SupportedCredentialSystemIdSchema,
    ThirdPartySystemId,
    formatThirdPartySystemName,
} from '@pillar-portal/api-types';
import { resolveCredentialDefaultsBySystemId } from './credential-defaults';
import { CredentialStatusBadge } from './CredentialCardView';
import type { CredentialTargetFamily, GenericWebsiteCredentialOption } from './SecretsCard';

const log = createComponentLogger('SecretModal');
const MaskedSecretFormValue = '••••••••••••••••';

export const SecretFormSupportedCredentialSystemIdSchema: z.ZodType<
    SupportedCredentialSystemId,
    number
> = z
    .number()
    .refine(
        (value): value is SupportedCredentialSystemId =>
            SupportedCredentialSystemIdSchema.safeParse(value).success
    );

type SecretFormSystemIdInput = number | string | '';

export const SecretFormSystemIdStringSchema: z.ZodType<SupportedCredentialSystemId, string> = z
    .string()
    .min(1)
    .transform((value: string) => Number(value))
    .pipe(SecretFormSupportedCredentialSystemIdSchema);

export const SecretFormSystemIdInputSchema: z.ZodType<
    SupportedCredentialSystemId,
    SecretFormSystemIdInput
> = z.union([SecretFormSupportedCredentialSystemIdSchema, SecretFormSystemIdStringSchema]);

const DefaultSupportedCredentialSystemId: SupportedCredentialSystemId = ThirdPartySystemId.VALOR_US;
const defaultableCredentialFields = ['domain', 'channel'] as const;
type DefaultableCredentialField = (typeof defaultableCredentialFields)[number];
const AllCredentialTypes: readonly CredentialType[] = [
    CredentialType.VIRTUAL_TERMINAL,
    CredentialType.PHYSICAL_TERMINAL,
    CredentialType.IPX,
];
const IpgCredentialTypes: readonly CredentialType[] = [
    CredentialType.VIRTUAL_TERMINAL,
    CredentialType.PHYSICAL_TERMINAL,
];
const IpxWebsiteCredentialTypes: readonly CredentialType[] = [CredentialType.IPX];

export type SecretFormCredentials = Record<string, string>;

type SecretFormCredentialOwnerScope = CredentialOwnerScope | '';

export interface SecretFormData {
    system_id: SupportedCredentialSystemId;
    account_id: string;
    credential_type: CredentialType;
    credentials: SecretFormCredentials;
    applies_to_children: boolean;
    credential_owner_scope: SecretFormCredentialOwnerScope;
    ipx_credential_definition_uuid: string;
    status: Status;
}

export type SecretFormInput = Omit<SecretFormData, 'system_id'> & {
    system_id: SecretFormSystemIdInput;
};

export const SecretFormCredentialsSchema: z.ZodRecord<z.ZodString, z.ZodString> = z.record(
    z.string(),
    z.string().trim()
);

export const secretFormSchema: z.ZodType<SecretFormData, SecretFormInput> = z.object({
    system_id: SecretFormSystemIdInputSchema,
    account_id: z.string().min(1, 'Account ID is required'),
    credential_type: z.enum(CredentialType),
    credentials: SecretFormCredentialsSchema,
    applies_to_children: z.boolean(),
    credential_owner_scope: z.union([z.enum(CredentialOwnerScope), z.literal('')]),
    ipx_credential_definition_uuid: z.string(),
    status: z.enum(Status),
}) satisfies z.ZodType<SecretFormData>;

export const CredentialStringFieldSchema: z.ZodString = z.string();
export const CredentialFieldsRecordSchema: z.ZodRecord<z.ZodString, z.ZodUnknown> = z.record(
    z.string(),
    z.unknown()
);

const getDefaultSecretFormValues = (
    defaultSystemId: SecretFormSystemIdInput = DefaultSupportedCredentialSystemId,
    defaultCredentialType: CredentialType = CredentialType.VIRTUAL_TERMINAL
): SecretFormInput => ({
    system_id: defaultSystemId,
    account_id: '',
    credential_type: defaultCredentialType,
    credentials: {
        username: '',
        password: '',
    },
    applies_to_children: false,
    credential_owner_scope: '',
    ipx_credential_definition_uuid: '',
    status: Status.ACTIVE,
});

function resolveDefaultCredentialSystemId(
    credentialSystemOptions: readonly SupportedCredentialSystemId[],
    defaultCredentialType: CredentialType
): SecretFormSystemIdInput {
    if (credentialSystemOptions.length === 1) {
        return credentialSystemOptions[0] ?? '';
    }
    if (
        defaultCredentialType === CredentialType.IPX &&
        credentialSystemOptions.length <= 1 &&
        credentialSystemOptions.includes(ThirdPartySystemId.GENERIC)
    ) {
        return ThirdPartySystemId.GENERIC;
    }
    return '';
}

function getCredentialTypeOptionsForTargetFamily(
    credentialTargetFamily: CredentialTargetFamily
): readonly CredentialType[] {
    if (credentialTargetFamily === 'ipg') {
        return IpgCredentialTypes;
    }
    if (credentialTargetFamily === 'ipw' || credentialTargetFamily === 'generic_website') {
        return IpxWebsiteCredentialTypes;
    }
    return AllCredentialTypes;
}

export interface SecretModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    organizationId?: number;
    organizationUuid?: string;
    organizationName: string;
    secret?: SecretModalSecretReference | undefined;
    mode: 'view' | 'edit' | 'create';
    lockedCredentialType?: CredentialType;
    onSecretSaved?: (savedSecret: CreateSecretResponse) => void;
    credentialTargetFamily?: CredentialTargetFamily;
    credentialSystemSelectLabel?: string;
    availableCredentialSystemIds?: readonly SupportedCredentialSystemId[];
    initialCredentialSystemId?: SupportedCredentialSystemId;
    genericWebsiteOptions?: readonly GenericWebsiteCredentialOption[];
}

type SecretPublicFieldFallback = SecretUuidReference &
    Pick<
        SecretListItem,
        | 'system_id'
        | 'account_id'
        | 'credential_type'
        | 'credential_definition_key'
        | 'public_fields'
        | 'applies_to_children'
        | 'status'
        | 'pending_reason'
    >;

type SecretModalSecretReference = SecretUuidReference | SecretPublicFieldFallback;

type SecretFormHydrationSource = {
    readonly system_id: SupportedCredentialSystemId;
    readonly account_id: string;
    readonly credential_type: CredentialType;
    readonly credential_definition_key: string;
    readonly credentials: unknown;
    readonly applies_to_children: boolean;
    readonly status: Status;
    readonly pending_reason: CredentialPendingReason | null;
    readonly ipx_credential_definition_uuid: string | null;
};

function isSecretPublicFieldFallback(
    secret: SecretModalSecretReference | undefined
): secret is SecretPublicFieldFallback {
    return (
        secret !== undefined &&
        'system_id' in secret &&
        'account_id' in secret &&
        'credential_type' in secret &&
        'credential_definition_key' in secret &&
        'public_fields' in secret &&
        'applies_to_children' in secret &&
        'status' in secret
    );
}

function isCreateSecretResponse(
    response: CreateSecretResponse | SecretMetadataResponse
): response is CreateSecretResponse {
    return 'organization_uuid' in response;
}

function isSaveSecretResponse(
    response: CreateSecretResponse | SecretMetadataResponse
): response is SecretMetadataResponse {
    return 'credential_uuid' in response;
}

function getCredentialStringField(credentials: unknown, fieldName: string): string {
    const recordParseResult = CredentialFieldsRecordSchema.safeParse(credentials);
    if (!recordParseResult.success) {
        return '';
    }
    const value = recordParseResult.data[fieldName];
    const parseResult = CredentialStringFieldSchema.safeParse(value);
    return parseResult.success ? parseResult.data : '';
}

function buildSecretFormCredentials(
    fieldNames: readonly string[],
    credentials: unknown
): SecretFormCredentials {
    const recordParseResult = CredentialFieldsRecordSchema.safeParse(credentials);
    const resolvedFieldNames =
        fieldNames.length > 0
            ? fieldNames
            : recordParseResult.success
              ? Object.keys(recordParseResult.data)
              : [];
    const formCredentials: SecretFormCredentials = {};
    for (const fieldName of resolvedFieldNames) {
        formCredentials[fieldName] = getCredentialStringField(credentials, fieldName);
    }
    return formCredentials;
}

function buildSecretFormHydrationKey(
    mode: SecretModalProps['mode'],
    secret: SecretModalSecretReference | undefined,
    source: SecretFormHydrationSource
): string {
    return stringifyLosslessJson([
        mode,
        secret?.credential_uuid ?? null,
        source.system_id,
        source.account_id,
        source.credential_type,
        source.credential_definition_key,
        source.applies_to_children,
        source.status,
    ]);
}

function listCredentialFieldDescriptorNames(
    definition: SecretCredentialDefinitionListItem | null
): readonly string[] {
    return definition?.field_descriptors.map((descriptor) => descriptor.field_name) ?? [];
}

function listRequiredCredentialFieldNames(
    definition: SecretCredentialDefinitionListItem | null
): readonly string[] {
    return (
        definition?.field_descriptors
            .filter((descriptor) => descriptor.required)
            .map((descriptor) => descriptor.field_name) ?? []
    );
}

function getCredentialDefaultValue(
    credentialDefaults: Readonly<Partial<Record<DefaultableCredentialField, string>>>,
    fieldName: string
): string | undefined {
    for (const defaultableField of defaultableCredentialFields) {
        if (defaultableField === fieldName) {
            return credentialDefaults[defaultableField];
        }
    }
    return undefined;
}

function getSecretCredentialDefinitionLabel(
    definition: SecretCredentialDefinitionListItem
): string {
    if (definition.credential_definition_key.length === 0) {
        return definition.name;
    }
    return `${definition.name} (${definition.credential_definition_key})`;
}

function getIpxCredentialDefinitionLabel(definition: IpxCredentialDefinitionProjection): string {
    if (definition.credential_definition_key.length === 0) {
        return formatThirdPartySystemName(definition.system_id);
    }
    return `${formatThirdPartySystemName(definition.system_id)} (${definition.credential_definition_key})`;
}

function getCredentialOwnerScopeDisplayName(ownerScope: CredentialOwnerScope): string {
    switch (ownerScope) {
        case CredentialOwnerScope.ROLE_ASSIGNMENT:
            return 'Current access role';
        case CredentialOwnerScope.ORGANIZATION:
            return 'Organization';
    }
}

function buildCredentialCandidateFields(
    definition: SecretCredentialDefinitionListItem,
    formCredentials: SecretFormCredentials,
    credentialDefaults: Readonly<Partial<Record<DefaultableCredentialField, string>>>
): Record<string, string> {
    const credentials: Record<string, string> = {};
    for (const fieldName of listCredentialFieldDescriptorNames(definition)) {
        const trimmedValue = (formCredentials[fieldName] ?? '').trim();
        if (trimmedValue.length > 0 && trimmedValue !== MaskedSecretFormValue) {
            credentials[fieldName] = trimmedValue;
        }
    }
    for (const fieldName of defaultableCredentialFields) {
        const defaultValue = credentialDefaults[fieldName]?.trim();
        if (defaultValue !== undefined && defaultValue.length > 0) {
            credentials[fieldName] ??= defaultValue;
        }
    }
    return credentials;
}

export function SecretModal({
    open,
    onOpenChange,
    organizationUuid,
    organizationName,
    secret,
    mode,
    lockedCredentialType,
    onSecretSaved,
    credentialTargetFamily = 'all',
    credentialSystemSelectLabel,
    availableCredentialSystemIds,
    initialCredentialSystemId,
    genericWebsiteOptions = [],
}: SecretModalProps) {
    const [isFormMode, setIsFormMode] = useState(mode !== 'view');
    const [revealedCredentialFieldNames, setRevealedCredentialFieldNames] = useState<
        ReadonlySet<string>
    >(() => new Set<string>());
    const [credentialFieldRevealErrors, setCredentialFieldRevealErrors] = useState<
        Readonly<Record<string, string>>
    >({});
    const [pendingCredentialFieldNames, setPendingCredentialFieldNames] = useState<
        ReadonlySet<string>
    >(() => new Set<string>());
    const fetchedCredentialFieldValuesRef = useRef<Record<string, string>>({});
    const credentialFieldRevealGenerationRef = useRef(0);
    const credentialTestGenerationRef = useRef(0);
    const credentialTestPendingRef = useRef(false);
    const [credentialTestResult, setCredentialTestResult] = useState<CredentialTestResult | null>(
        null
    );
    const [credentialTestErrorMessage, setCredentialTestErrorMessage] = useState<string | null>(
        null
    );
    const [isCredentialTestPending, setIsCredentialTestPending] = useState(false);
    const [postSaveErrorMessage, setPostSaveErrorMessage] = useState<string | null>(null);
    const handledSaveResponseRef = useRef<CreateSecretResponse | SecretMetadataResponse | null>(
        null
    );
    const hydratedSecretFormKeyRef = useRef<string | null>(null);
    const validatedOrganizationUuid = useMemo(() => {
        if (organizationUuid === undefined || organizationUuid === null) {
            return null;
        }
        const parseResult = z.string().uuid().safeParse(organizationUuid);
        return parseResult.success ? parseResult.data : null;
    }, [organizationUuid]);
    const { data: organizationData } = useQuery<GetOrganizationResponse>({
        queryKey: ['secret-modal', 'organization', validatedOrganizationUuid],
        queryFn: async () => {
            if (validatedOrganizationUuid === null) {
                throw new Error('Organization UUID is required');
            }
            const response = await organizationApi.getOrganization(validatedOrganizationUuid);
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: open && mode === 'create' && validatedOrganizationUuid !== null,
        retry: 0,
    });
    const { data: organizationConfigData } = useQuery<MerchantConfig>({
        queryKey: ['secret-modal', 'organization-config', validatedOrganizationUuid],
        queryFn: async () => {
            if (validatedOrganizationUuid === null) {
                throw new Error('Organization UUID is required');
            }
            const response = await sambaConfigApi.getOrgConfig(validatedOrganizationUuid);
            return MerchantConfigSchema.parse(response);
        },
        enabled: open && validatedOrganizationUuid !== null,
        retry: 0,
    });
    const { data: activeRoleAssignmentResponse } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        enabled: open,
        retry: 0,
    });
    const activeRoleAssignmentOperationalResponse = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentResponse
    );

    const secretIdentityInput =
        secret !== undefined && secret.credential_uuid !== undefined
            ? {
                  credential_uuid: secret.credential_uuid,
              }
            : undefined;
    const secretIdentityForUpdate =
        secret !== undefined && secret.credential_uuid !== undefined
            ? {
                  credential_uuid: secret.credential_uuid,
              }
            : undefined;

    const {
        data: secretWithCredentials,
        isLoading: isLoadingSecret,
        error: loadError,
        refetch: retryLoadSecret,
    } = useSecret(mode === 'view' || mode === 'edit' ? secretIdentityInput : undefined);
    const secretPublicFieldFallback = useMemo(
        () => (isSecretPublicFieldFallback(secret) ? secret : null),
        [secret]
    );
    const isUsingPublicFieldFallback =
        loadError instanceof ApiError &&
        loadError.status === 422 &&
        secretWithCredentials === undefined &&
        secretPublicFieldFallback !== null &&
        (mode === 'view' || mode === 'edit');
    const secretFormHydrationSource: SecretFormHydrationSource | null = useMemo(() => {
        if (secretWithCredentials !== undefined) {
            return {
                system_id: secretWithCredentials.system_id,
                account_id: secretWithCredentials.account_id,
                credential_type: secretWithCredentials.credential_type,
                credential_definition_key: secretWithCredentials.credential_definition_key,
                credentials: secretWithCredentials.credentials,
                applies_to_children: secretWithCredentials.applies_to_children,
                status: secretWithCredentials.status,
                pending_reason: secretWithCredentials.pending_reason ?? null,
                ipx_credential_definition_uuid:
                    secretWithCredentials.ipx_credential_definition_uuid,
            };
        }
        if (isUsingPublicFieldFallback) {
            return {
                system_id: secretPublicFieldFallback.system_id,
                account_id: secretPublicFieldFallback.account_id,
                credential_type: secretPublicFieldFallback.credential_type,
                credential_definition_key: secretPublicFieldFallback.credential_definition_key,
                credentials: secretPublicFieldFallback.public_fields,
                applies_to_children: secretPublicFieldFallback.applies_to_children,
                status: secretPublicFieldFallback.status,
                pending_reason: secretPublicFieldFallback.pending_reason ?? null,
                ipx_credential_definition_uuid: null,
            };
        }
        return null;
    }, [isUsingPublicFieldFallback, secretPublicFieldFallback, secretWithCredentials]);

    const saveMutation = useSaveSecret(secretIdentityForUpdate);
    const credentialSystemOptions: readonly SupportedCredentialSystemId[] =
        availableCredentialSystemIds ?? SupportedCredentialSystemIds;
    const credentialTypeOptions: readonly CredentialType[] =
        getCredentialTypeOptionsForTargetFamily(credentialTargetFamily);
    const defaultCredentialType: CredentialType =
        lockedCredentialType !== undefined && credentialTypeOptions.includes(lockedCredentialType)
            ? lockedCredentialType
            : (credentialTypeOptions[0] ?? CredentialType.VIRTUAL_TERMINAL);
    const defaultFormSystemId: SecretFormSystemIdInput =
        initialCredentialSystemId !== undefined &&
        credentialSystemOptions.includes(initialCredentialSystemId)
            ? initialCredentialSystemId
            : resolveDefaultCredentialSystemId(credentialSystemOptions, defaultCredentialType);
    const resolvedCredentialSystemSelectLabel: string = credentialSystemSelectLabel ?? 'System ID';

    const {
        register,
        handleSubmit,
        formState: { errors, dirtyFields },
        reset,
        watch,
        setValue,
        getValues,
    } = useForm<SecretFormInput, unknown, SecretFormData>({
        resolver: zodResolver(secretFormSchema),
        defaultValues: getDefaultSecretFormValues(defaultFormSystemId),
    });

    const appliesToChildren = watch('applies_to_children');
    const watchedStatus = watch('status');
    const isActive = watchedStatus === Status.ACTIVE;
    const watchedCredentials = watch('credentials');
    const selectedCredentialType = watch('credential_type');
    const selectedSystemIdParseResult = SecretFormSystemIdInputSchema.safeParse(watch('system_id'));
    const selectedSystemId: SupportedCredentialSystemId | null = selectedSystemIdParseResult.success
        ? selectedSystemIdParseResult.data
        : null;
    const isSystemIdSelectionInvalid: boolean =
        mode === 'create' &&
        credentialTargetFamily !== 'generic_website' &&
        selectedSystemId === null;
    const selectedCredentialOwnerScope = watch('credential_owner_scope');
    const selectedIpxCredentialDefinitionUuid = watch('ipx_credential_definition_uuid');
    const credentialDefaults = useMemo(
        () =>
            selectedSystemId === null
                ? {}
                : resolveCredentialDefaultsBySystemId(selectedSystemId, organizationConfigData),
        [organizationConfigData, selectedSystemId]
    );
    const credentialDefinitionOrganizationUuid =
        validatedOrganizationUuid ?? organizationData?.organization_uuid ?? null;
    const {
        data: secretCredentialDefinitionsData,
        isLoading: isLoadingSecretCredentialDefinitions,
        error: secretCredentialDefinitionsError,
    } = useQuery<ListSecretCredentialDefinitionsResponse>({
        queryKey: [
            'secret-modal',
            'secret-credential-definitions',
            credentialDefinitionOrganizationUuid,
            credentialSystemOptions,
            credentialTypeOptions,
        ],
        queryFn: async () => {
            if (credentialDefinitionOrganizationUuid === null) {
                throw new Error('Organization UUID is required');
            }
            return await secretsApi.listCredentialDefinitions({
                organization_uuid: credentialDefinitionOrganizationUuid,
                system_ids: [...credentialSystemOptions],
                credential_types: [...credentialTypeOptions],
            });
        },
        enabled: open && credentialDefinitionOrganizationUuid !== null,
        retry: 0,
    });
    const availableSecretCredentialDefinitions = useMemo(
        () =>
            (secretCredentialDefinitionsData?.definitions ?? []).filter(
                (definition) =>
                    selectedSystemId !== null &&
                    definition.system_id === selectedSystemId &&
                    definition.credential_type === selectedCredentialType
            ),
        [secretCredentialDefinitionsData, selectedCredentialType, selectedSystemId]
    );
    const selectedSecretCredentialDefinition = useMemo(() => {
        return (
            availableSecretCredentialDefinitions.find(
                (definition) =>
                    definition.ipx_credential_definition_uuid ===
                    selectedIpxCredentialDefinitionUuid
            ) ??
            availableSecretCredentialDefinitions[0] ??
            null
        );
    }, [
        availableSecretCredentialDefinitions,
        selectedCredentialType,
        selectedIpxCredentialDefinitionUuid,
    ]);
    const hydrationSecretCredentialDefinition = useMemo(() => {
        if (secretFormHydrationSource === null) {
            return null;
        }
        return (
            (secretCredentialDefinitionsData?.definitions ?? []).find((definition) =>
                secretFormHydrationSource.ipx_credential_definition_uuid !== null
                    ? definition.ipx_credential_definition_uuid ===
                      secretFormHydrationSource.ipx_credential_definition_uuid
                    : definition.system_id === secretFormHydrationSource.system_id &&
                      definition.credential_type === secretFormHydrationSource.credential_type &&
                      definition.credential_definition_key ===
                          secretFormHydrationSource.credential_definition_key
            ) ?? null
        );
    }, [secretCredentialDefinitionsData, secretFormHydrationSource]);
    const activeRoleScope =
        activeRoleAssignmentOperationalResponse?.organization_uuid !== null &&
        activeRoleAssignmentOperationalResponse?.organization_uuid !== undefined
            ? {
                  organization_uuid: activeRoleAssignmentOperationalResponse.organization_uuid,
                  role_assignment_uuid:
                      activeRoleAssignmentOperationalResponse.role_assignment_uuid,
              }
            : null;
    const {
        data: ipxCredentialBootstrap,
        isLoading: isLoadingIpxCredentialDefinitions,
        error: ipxCredentialDefinitionsError,
    } = useQuery<IpxCredentialRuntimeBootstrapResponse>({
        queryKey: [
            'secret-modal',
            'ipx-credential-definitions',
            activeRoleScope?.organization_uuid,
            activeRoleScope?.role_assignment_uuid,
        ],
        queryFn: async () => {
            if (activeRoleScope === null) {
                throw new Error('Active organization scope is required');
            }
            return await rpaCredentialInjectionApi.bootstrap({
                role_scope: activeRoleScope,
            });
        },
        enabled: open && selectedCredentialType === CredentialType.IPX && activeRoleScope !== null,
        retry: 0,
    });
    const availableIpxCredentialDefinitions = useMemo(() => {
        const definitionsByUuid = new Map<string, IpxCredentialDefinitionProjection>();
        for (const projection of ipxCredentialBootstrap?.workflow_projections ?? []) {
            if (selectedSystemId === null || projection.definition.system_id !== selectedSystemId) {
                continue;
            }
            definitionsByUuid.set(
                projection.definition.ipx_credential_definition_uuid,
                projection.definition
            );
        }
        return [...definitionsByUuid.values()].sort((left, right) =>
            getIpxCredentialDefinitionLabel(left).localeCompare(
                getIpxCredentialDefinitionLabel(right)
            )
        );
    }, [ipxCredentialBootstrap, selectedSystemId]);
    const selectedIpxCredentialDefinition = useMemo(
        () =>
            availableIpxCredentialDefinitions.find(
                (definition) =>
                    definition.ipx_credential_definition_uuid ===
                    selectedIpxCredentialDefinitionUuid
            ) ?? null,
        [availableIpxCredentialDefinitions, selectedIpxCredentialDefinitionUuid]
    );
    const selectedIpxCredentialWorkflowProjection = useMemo(
        () =>
            ipxCredentialBootstrap?.workflow_projections.find(
                (projection) =>
                    projection.definition.ipx_credential_definition_uuid ===
                    selectedIpxCredentialDefinitionUuid
            ) ?? null,
        [ipxCredentialBootstrap, selectedIpxCredentialDefinitionUuid]
    );
    const selectedIpxCredentialDefinitionOwnerScope =
        selectedCredentialType === CredentialType.IPX
            ? (selectedSecretCredentialDefinition?.credential_owner_scope ?? null)
            : null;

    const requiredFields: readonly string[] = listRequiredCredentialFieldNames(
        selectedSecretCredentialDefinition
    );
    const missingCredentialFields: string[] = requiredFields.filter((fieldName) => {
        if (fieldName in watchedCredentials) {
            void fieldName;
            // TODO: we have to tighten the typing here
            // eslint-disable-next-line no-restricted-syntax
            const value = watchedCredentials[fieldName as keyof typeof watchedCredentials];
            return value === undefined || value.trim().length === 0;
        }
        return true;
    });
    const wouldSaveActiveIpxCredential =
        selectedCredentialType === CredentialType.IPX &&
        isActive &&
        missingCredentialFields.length === 0;
    const duplicateActiveIpxCredential =
        wouldSaveActiveIpxCredential &&
        selectedCredentialOwnerScope !== '' &&
        selectedIpxCredentialWorkflowProjection?.credential !== null &&
        selectedIpxCredentialWorkflowProjection?.credential !== undefined &&
        selectedIpxCredentialWorkflowProjection.credential.credential_owner_scope ===
            selectedCredentialOwnerScope &&
        selectedIpxCredentialWorkflowProjection.credential.credential_uuid !==
            secretIdentityForUpdate?.credential_uuid
            ? selectedIpxCredentialWorkflowProjection.credential
            : null;
    const duplicateActiveIpxCredentialMessage =
        duplicateActiveIpxCredential === null
            ? null
            : `An active credential already exists for this definition and scope: ${duplicateActiveIpxCredential.account_id}. Edit or disable that credential before saving another active credential.`;

    const credentialTestInputSignature = stringifyLosslessJson([
        open,
        mode,
        secretIdentityForUpdate?.credential_uuid ?? null,
        credentialDefinitionOrganizationUuid,
        selectedSystemId,
        selectedCredentialType,
        selectedIpxCredentialDefinitionUuid,
        selectedSecretCredentialDefinition?.credential_definition_key ?? null,
        watchedCredentials,
    ]);

    useEffect(() => {
        credentialTestGenerationRef.current += 1;
        credentialTestPendingRef.current = false;
        setIsCredentialTestPending(false);
        setCredentialTestResult(null);
        setCredentialTestErrorMessage(null);
    }, [credentialTestInputSignature]);

    // Update form when secret data is available and modal is open.
    // Including `open` ensures we rehydrate the form on every reopen,
    // even when React Query returns cached data with stable reference identity.
    useEffect(() => {
        if (!open) {
            return;
        }
        if (secretFormHydrationSource && (mode === 'view' || mode === 'edit')) {
            const hydrationKey = buildSecretFormHydrationKey(
                mode,
                secret,
                secretFormHydrationSource
            );
            if (hydratedSecretFormKeyRef.current === hydrationKey) {
                return;
            }
            const systemIdParseResult = SecretFormSupportedCredentialSystemIdSchema.safeParse(
                secretFormHydrationSource.system_id
            );
            if (!systemIdParseResult.success) {
                log.error(
                    'Invalid system_id returned from secret lookup',
                    systemIdParseResult.error
                );
                throw new Error(
                    `Invalid system_id returned from secret lookup: ${systemIdParseResult.error.message}`
                );
            }
            const validatedSystemId = systemIdParseResult.data;
            hydratedSecretFormKeyRef.current = hydrationKey;
            reset({
                system_id: validatedSystemId,
                account_id: secretFormHydrationSource.account_id,
                credential_type: secretFormHydrationSource.credential_type,
                credentials: buildSecretFormCredentials(
                    listCredentialFieldDescriptorNames(hydrationSecretCredentialDefinition),
                    secretFormHydrationSource.credentials
                ),
                applies_to_children: secretFormHydrationSource.applies_to_children,
                credential_owner_scope: '',
                ipx_credential_definition_uuid:
                    hydrationSecretCredentialDefinition?.ipx_credential_definition_uuid ?? '',
                status: secretFormHydrationSource.status,
            });
        }
    }, [open, secretFormHydrationSource, mode, secret, reset, hydrationSecretCredentialDefinition]);

    // Reset form mode when modal opens/closes or mode changes
    useEffect(() => {
        setIsFormMode(mode !== 'view');
        if (open && mode === 'create') {
            const defaultValues: SecretFormInput = getDefaultSecretFormValues(
                defaultFormSystemId,
                defaultCredentialType
            );
            reset(defaultValues);
        }
        if (!open) {
            credentialFieldRevealGenerationRef.current += 1;
            for (const fieldName of Object.keys(fetchedCredentialFieldValuesRef.current)) {
                const fetchedValue = fetchedCredentialFieldValuesRef.current[fieldName];
                if (getValues(`credentials.${fieldName}`) === fetchedValue) {
                    setValue(`credentials.${fieldName}`, '');
                }
            }
            fetchedCredentialFieldValuesRef.current = {};
            setRevealedCredentialFieldNames(new Set<string>());
            setCredentialFieldRevealErrors({});
            setPendingCredentialFieldNames(new Set<string>());
            hydratedSecretFormKeyRef.current = null;
            reset(getDefaultSecretFormValues(defaultFormSystemId, defaultCredentialType));
            // Don't reset mutation - React Query handles cleanup on unmount
            // Resetting during unmount/close causes React error #185 (updating unmounted component)
        }
    }, [defaultFormSystemId, defaultCredentialType, getValues, open, mode, reset, setValue]);

    useEffect(() => {
        if (!open || (mode === 'view' && !isFormMode)) {
            setRevealedCredentialFieldNames(new Set<string>());
        }
    }, [isFormMode, mode, open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (selectedCredentialType !== CredentialType.IPX) {
            if (availableSecretCredentialDefinitions.length === 0) {
                if (selectedIpxCredentialDefinitionUuid.length > 0) {
                    setValue('ipx_credential_definition_uuid', '');
                }
            } else {
                const nextDefinition =
                    selectedSecretCredentialDefinition ?? availableSecretCredentialDefinitions[0];
                if (
                    nextDefinition !== undefined &&
                    selectedIpxCredentialDefinitionUuid !==
                        nextDefinition.ipx_credential_definition_uuid
                ) {
                    setValue(
                        'ipx_credential_definition_uuid',
                        nextDefinition.ipx_credential_definition_uuid,
                        { shouldValidate: true }
                    );
                }
            }
            if (selectedCredentialOwnerScope.length > 0) {
                setValue('credential_owner_scope', '');
            }
            return;
        }
        if (availableIpxCredentialDefinitions.length === 0) {
            if (selectedIpxCredentialDefinitionUuid.length > 0) {
                setValue('ipx_credential_definition_uuid', '');
            }
            if (selectedCredentialOwnerScope.length > 0) {
                setValue('credential_owner_scope', '');
            }
            return;
        }
        const nextDefinition =
            selectedIpxCredentialDefinition ?? availableIpxCredentialDefinitions[0];
        if (nextDefinition === undefined) {
            return;
        }
        const nextSecretDefinition =
            availableSecretCredentialDefinitions.find(
                (definition) =>
                    definition.ipx_credential_definition_uuid ===
                    nextDefinition.ipx_credential_definition_uuid
            ) ?? null;
        const nextOwnerScope = nextSecretDefinition?.credential_owner_scope ?? null;
        if (selectedIpxCredentialDefinitionUuid !== nextDefinition.ipx_credential_definition_uuid) {
            setValue(
                'ipx_credential_definition_uuid',
                nextDefinition.ipx_credential_definition_uuid,
                { shouldValidate: true }
            );
        }
        if (nextOwnerScope !== null && selectedCredentialOwnerScope !== nextOwnerScope) {
            setValue('credential_owner_scope', nextOwnerScope, {
                shouldValidate: true,
            });
        }
    }, [
        availableIpxCredentialDefinitions,
        availableSecretCredentialDefinitions,
        open,
        selectedCredentialOwnerScope,
        selectedCredentialType,
        selectedIpxCredentialDefinition,
        selectedIpxCredentialDefinitionUuid,
        selectedSecretCredentialDefinition,
        setValue,
    ]);

    useEffect(() => {
        if (!open || mode !== 'create') {
            return;
        }
        if (
            credentialSystemOptions.length === 1 &&
            selectedSystemId !== credentialSystemOptions[0]
        ) {
            setValue('system_id', credentialSystemOptions[0] ?? '', {
                shouldValidate: true,
            });
            return;
        }
        if (
            credentialSystemOptions.length !== 1 &&
            selectedSystemId !== null &&
            !credentialSystemOptions.includes(selectedSystemId)
        ) {
            setValue('system_id', defaultFormSystemId, { shouldValidate: true });
        }
    }, [credentialSystemOptions, defaultFormSystemId, mode, open, selectedSystemId, setValue]);

    useEffect(() => {
        if (!open || mode !== 'create') {
            return;
        }
        if (!credentialTypeOptions.includes(selectedCredentialType)) {
            setValue('credential_type', defaultCredentialType, { shouldValidate: true });
        }
    }, [
        credentialTypeOptions,
        defaultCredentialType,
        mode,
        open,
        selectedCredentialType,
        setValue,
    ]);

    // Reset mutation when modal opens to ensure clean state
    // Do this separately to avoid dependency issues
    useEffect(() => {
        if (open) {
            saveMutation.reset();
            setPostSaveErrorMessage(null);
        } else {
            hydratedSecretFormKeyRef.current = null;
        }
        // saveMutation is intentionally excluded to avoid reset loops
    }, [open]);

    // Handle successful save
    useEffect(() => {
        if (!saveMutation.isSuccess || saveMutation.data === undefined) {
            return;
        }
        const response = saveMutation.data;
        if (handledSaveResponseRef.current === response) {
            return;
        }
        handledSaveResponseRef.current = response;
        try {
            if (
                mode === 'create' &&
                onSecretSaved !== undefined &&
                isCreateSecretResponse(response)
            ) {
                onSecretSaved(response);
            }
            if (mode === 'edit' && onSecretSaved !== undefined && isSaveSecretResponse(response)) {
                onSecretSaved(response);
            }
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Failed to attach newly created secret';
            log.error('Post-save callback failed', error);
            setPostSaveErrorMessage(message);
            return;
        }
        onOpenChange(false);
    }, [saveMutation.isSuccess, saveMutation.data, onOpenChange, onSecretSaved]);

    const onSubmit = async (data: SecretFormData) => {
        if (duplicateActiveIpxCredentialMessage !== null) {
            return;
        }
        if (selectedSecretCredentialDefinition === null) {
            setPostSaveErrorMessage('Select a credential definition before saving credentials.');
            return;
        }
        const credentials = buildCredentialCandidateFields(
            selectedSecretCredentialDefinition,
            data.credentials,
            credentialDefaults
        );
        if (data.ipx_credential_definition_uuid.length === 0) {
            setPostSaveErrorMessage('Select a credential definition before saving credentials.');
            return;
        }
        let credentialDefinitionRequestFields: {
            readonly ipx_credential_definition_uuid: string;
            readonly credential_owner_scope?: CredentialOwnerScope;
        } = {
            ipx_credential_definition_uuid: data.ipx_credential_definition_uuid,
        };
        if (data.credential_type === CredentialType.IPX) {
            const credentialOwnerScope = data.credential_owner_scope;
            if (credentialOwnerScope === '') {
                setPostSaveErrorMessage(
                    'Select a credential definition before saving credentials.'
                );
                return;
            }
            credentialDefinitionRequestFields = {
                credential_owner_scope: credentialOwnerScope,
                ipx_credential_definition_uuid: data.ipx_credential_definition_uuid,
            };
        }

        if (mode === 'create') {
            const resolvedOrganizationUuid: string | undefined =
                validatedOrganizationUuid ?? organizationData?.organization_uuid;
            if (resolvedOrganizationUuid === undefined) {
                setPostSaveErrorMessage(
                    'Unable to create secret: organization UUID is unavailable.'
                );
                return;
            }
            saveMutation.mutate({
                organization_uuid: resolvedOrganizationUuid,
                system_id: data.system_id,
                account_id: data.account_id,
                credential_type: data.credential_type,
                credentials,
                applies_to_children: data.applies_to_children,
                ...credentialDefinitionRequestFields,
            });
        } else if (mode === 'edit') {
            if (secretIdentityForUpdate === undefined) {
                throw new Error('Unable to edit secret: credential_uuid is unavailable.');
            }
            saveMutation.mutate({
                system_id: data.system_id,
                account_id: data.account_id,
                credential_type: data.credential_type,
                credentials,
                applies_to_children: data.applies_to_children,
                status: data.status,
                ...credentialDefinitionRequestFields,
            });
        }
    };

    const credentialTestCandidateFields =
        selectedSecretCredentialDefinition === null
            ? null
            : buildCredentialCandidateFields(
                  selectedSecretCredentialDefinition,
                  watchedCredentials,
                  credentialDefaults
              );
    const hasCredentialTestCandidate =
        selectedSecretCredentialDefinition !== null &&
        listCredentialFieldDescriptorNames(selectedSecretCredentialDefinition).some((fieldName) => {
            const value = (watchedCredentials[fieldName] ?? '').trim();
            return (
                dirtyFields.credentials?.[fieldName] === true &&
                value.length > 0 &&
                value !== MaskedSecretFormValue
            );
        });
    const canTestStoredCredential =
        mode === 'edit' &&
        !hasCredentialTestCandidate &&
        secretFormHydrationSource !== null &&
        secretFormHydrationSource.system_id === selectedSystemId &&
        secretFormHydrationSource.credential_type === selectedCredentialType &&
        secretFormHydrationSource.credential_definition_key ===
            selectedSecretCredentialDefinition?.credential_definition_key &&
        hydrationSecretCredentialDefinition !== null &&
        hydrationSecretCredentialDefinition.ipx_credential_definition_uuid ===
            selectedIpxCredentialDefinitionUuid;
    const credentialTestPrerequisiteMessage =
        mode === 'edit' &&
        secretFormHydrationSource !== null &&
        selectedSecretCredentialDefinition !== null &&
        !hasCredentialTestCandidate &&
        !canTestStoredCredential
            ? 'Enter credential fields for the selected authentication method before testing.'
            : null;
    const canTestCredential =
        mode === 'view'
            ? secretIdentityForUpdate !== undefined && !isLoadingSecret && loadError === null
            : selectedSystemId !== null &&
              selectedSecretCredentialDefinition !== null &&
              selectedIpxCredentialDefinitionUuid.length > 0 &&
              (mode === 'edit'
                  ? secretIdentityForUpdate !== undefined &&
                    (canTestStoredCredential || hasCredentialTestCandidate)
                  : credentialDefinitionOrganizationUuid !== null &&
                    missingCredentialFields.length === 0);

    const testCredential = async (): Promise<void> => {
        if (!canTestCredential || credentialTestPendingRef.current) {
            return;
        }
        let request: CredentialTestRequest;
        if (mode === 'view' || canTestStoredCredential) {
            if (secretIdentityForUpdate === undefined) {
                return;
            }
            request = {
                kind: CredentialTestRequestKind.STORED,
                credential_uuid: secretIdentityForUpdate.credential_uuid,
            };
        } else {
            if (
                selectedSystemId === null ||
                selectedSecretCredentialDefinition === null ||
                selectedIpxCredentialDefinitionUuid.length === 0 ||
                credentialTestCandidateFields === null
            ) {
                return;
            }
            const candidateFields = {
                system_id: selectedSystemId,
                credential_type: selectedCredentialType,
                ipx_credential_definition_uuid: selectedIpxCredentialDefinitionUuid,
                credential_definition_key:
                    selectedSecretCredentialDefinition.credential_definition_key,
                credentials: credentialTestCandidateFields,
            };
            if (mode === 'create') {
                if (credentialDefinitionOrganizationUuid === null) {
                    return;
                }
                request = {
                    kind: CredentialTestRequestKind.CREATE_CANDIDATE,
                    organization_uuid: credentialDefinitionOrganizationUuid,
                    ...candidateFields,
                };
            } else {
                if (secretIdentityForUpdate === undefined) {
                    return;
                }
                request = {
                    kind: CredentialTestRequestKind.EDIT_CANDIDATE,
                    credential_uuid: secretIdentityForUpdate.credential_uuid,
                    ...candidateFields,
                };
            }
        }

        const requestGeneration = credentialTestGenerationRef.current;
        credentialTestPendingRef.current = true;
        setIsCredentialTestPending(true);
        setCredentialTestResult(null);
        setCredentialTestErrorMessage(null);
        try {
            const result = await secretsApi.testCredential(request);
            if (credentialTestGenerationRef.current === requestGeneration) {
                setCredentialTestResult(result);
            }
        } catch (error) {
            if (credentialTestGenerationRef.current === requestGeneration) {
                setCredentialTestErrorMessage(
                    error instanceof Error ? error.message : 'Unable to test credential.'
                );
            }
        } finally {
            if (credentialTestGenerationRef.current === requestGeneration) {
                credentialTestPendingRef.current = false;
                setIsCredentialTestPending(false);
            }
        }
    };

    const handleMachineCredentialDefinitionChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        const nextDefinitionUuid = event.target.value;
        setValue('ipx_credential_definition_uuid', nextDefinitionUuid, { shouldValidate: true });
        const nextDefinition = availableSecretCredentialDefinitions.find(
            (definition) => definition.ipx_credential_definition_uuid === nextDefinitionUuid
        );
        if (nextDefinition === undefined) {
            return;
        }
        setValue(
            'credentials',
            buildSecretFormCredentials(
                listCredentialFieldDescriptorNames(nextDefinition),
                watchedCredentials
            ),
            { shouldValidate: true }
        );
    };

    const handleSwitchToEdit = () => {
        setIsFormMode(true);
    };

    const handleCancel = (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        // Always close the modal when Cancel is clicked
        onOpenChange(false);
    };

    const handleOpenChange = (open: boolean) => {
        try {
            onOpenChange(open);
        } catch (error) {
            log.error('Error in onOpenChange handler', error);
        }
    };

    const hideCredentialField = (fieldName: string): void => {
        credentialFieldRevealGenerationRef.current += 1;
        const fetchedValue = fetchedCredentialFieldValuesRef.current[fieldName];
        if (fetchedValue !== undefined && getValues(`credentials.${fieldName}`) === fetchedValue) {
            setValue(`credentials.${fieldName}`, '');
        }
        delete fetchedCredentialFieldValuesRef.current[fieldName];
        setRevealedCredentialFieldNames((currentFieldNames) => {
            const nextFieldNames = new Set<string>(currentFieldNames);
            nextFieldNames.delete(fieldName);
            return nextFieldNames;
        });
        setCredentialFieldRevealErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[fieldName];
            return nextErrors;
        });
    };

    const toggleCredentialFieldReveal = async (fieldName: string): Promise<void> => {
        if (revealedCredentialFieldNames.has(fieldName)) {
            hideCredentialField(fieldName);
            return;
        }
        const localValue = getValues(`credentials.${fieldName}`);
        const credentialUuid = secretIdentityForUpdate?.credential_uuid;
        if (
            localValue.length > 0 ||
            mode === 'create' ||
            credentialUuid === undefined ||
            isUsingPublicFieldFallback
        ) {
            setRevealedCredentialFieldNames((currentFieldNames) =>
                new Set<string>(currentFieldNames).add(fieldName)
            );
            return;
        }

        const requestGeneration = ++credentialFieldRevealGenerationRef.current;
        setCredentialFieldRevealErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            delete nextErrors[fieldName];
            return nextErrors;
        });
        setPendingCredentialFieldNames((currentFieldNames) =>
            new Set<string>(currentFieldNames).add(fieldName)
        );
        try {
            const response = await ipxCredentialManagementApi.revealPolicyCredentialSecretField({
                credential_uuid: credentialUuid,
                field_name: fieldName,
            });
            if (response.field_name !== fieldName) {
                throw new Error('Credential reveal response did not match the requested field');
            }
            if (credentialFieldRevealGenerationRef.current !== requestGeneration) {
                return;
            }
            fetchedCredentialFieldValuesRef.current[fieldName] = response.field_value;
            setValue(`credentials.${fieldName}`, response.field_value);
            setRevealedCredentialFieldNames((currentFieldNames) =>
                new Set<string>(currentFieldNames).add(fieldName)
            );
        } catch (error: unknown) {
            if (credentialFieldRevealGenerationRef.current === requestGeneration) {
                setCredentialFieldRevealErrors((currentErrors) => ({
                    ...currentErrors,
                    [fieldName]:
                        error instanceof Error
                            ? error.message
                            : 'Unable to reveal credential field',
                }));
            }
        } finally {
            setPendingCredentialFieldNames((currentFieldNames) => {
                const nextFieldNames = new Set<string>(currentFieldNames);
                nextFieldNames.delete(fieldName);
                return nextFieldNames;
            });
        }
    };

    // const handleOpenChange = (open: boolean | undefined) => {
    //     // Ensure we only pass boolean values to onOpenChange
    //     // Guard against undefined or non-boolean values that might come from Radix UI
    //     if (typeof open === 'boolean') {
    //         try {
    //             onOpenChange(open);
    //         } catch (error) {
    //             console.error('Error in onOpenChange handler:', error);
    //         }
    //     }
    // };

    const getTitle = () => {
        if (mode === 'create') return 'Create Credentials';
        if (mode === 'edit' || isFormMode) return 'Edit Credentials';
        return 'View Credentials';
    };

    const getDescription = () => {
        return `${mode === 'create' ? 'Add new' : isFormMode ? 'Update' : 'View'} API credentials for ${organizationName}`;
    };

    const renderCredentialFields = () => {
        if (isLoadingSecretCredentialDefinitions) {
            return (
                <div className="space-y-4 border-t pt-4 text-sm text-muted-foreground">
                    Loading credential fields...
                </div>
            );
        }
        if (secretCredentialDefinitionsError) {
            return (
                <Alert variant="destructive">
                    <AlertDescription>
                        {secretCredentialDefinitionsError instanceof Error
                            ? secretCredentialDefinitionsError.message
                            : 'Failed to load credential definitions'}
                    </AlertDescription>
                </Alert>
            );
        }
        if (selectedSecretCredentialDefinition === null) {
            return (
                <Alert variant="destructive">
                    <AlertDescription>
                        No credential definition is available for this system and credential type.
                    </AlertDescription>
                </Alert>
            );
        }

        return (
            <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">
                    {getSecretCredentialDefinitionLabel(selectedSecretCredentialDefinition)}
                </h4>
                {selectedSecretCredentialDefinition.field_descriptors.map(
                    (descriptor: IpxCredentialFieldDescriptor) => {
                        const fieldName = descriptor.field_name;
                        const fieldError = errors.credentials?.[fieldName];
                        const isSecretField: boolean =
                            descriptor.field_classification ===
                            IpxCredentialFieldClassification.SECRET;
                        const isSecretFieldRevealed: boolean =
                            revealedCredentialFieldNames.has(fieldName);
                        const isCredentialFieldRevealPending: boolean =
                            pendingCredentialFieldNames.has(fieldName);
                        const credentialFieldRevealError = credentialFieldRevealErrors[fieldName];
                        const placeholder =
                            getCredentialDefaultValue(credentialDefaults, fieldName) ??
                            descriptor.display.label;
                        return (
                            <div key={fieldName}>
                                <Label htmlFor={fieldName}>{descriptor.display.label}</Label>
                                <div className="flex items-center gap-2">
                                    {isViewOnly && isSecretField ? (
                                        <Input
                                            key={`masked-${fieldName}`}
                                            id={fieldName}
                                            value={MaskedSecretFormValue}
                                            placeholder={placeholder}
                                            disabled
                                            readOnly
                                            type="password"
                                            passwordToggle={false}
                                            autoComplete="off"
                                            spellCheck={false}
                                        />
                                    ) : (
                                        <Input
                                            key={`editable-${fieldName}`}
                                            id={fieldName}
                                            {...register(`credentials.${fieldName}`)}
                                            placeholder={placeholder}
                                            disabled={isViewOnly}
                                            type={
                                                isSecretField && !isSecretFieldRevealed
                                                    ? 'password'
                                                    : 'text'
                                            }
                                            passwordToggle={false}
                                            autoComplete={isSecretField ? 'new-password' : 'off'}
                                            spellCheck={false}
                                        />
                                    )}
                                    {isSecretField && !isViewOnly && !isUsingPublicFieldFallback ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            aria-label={
                                                isSecretFieldRevealed
                                                    ? `Hide ${descriptor.display.label}`
                                                    : `Show ${descriptor.display.label}`
                                            }
                                            title={
                                                isSecretFieldRevealed
                                                    ? `Hide ${descriptor.display.label}`
                                                    : `Show ${descriptor.display.label}`
                                            }
                                            disabled={isCredentialFieldRevealPending}
                                            onClick={() => {
                                                runInBackground(
                                                    toggleCredentialFieldReveal(fieldName),
                                                    () => undefined
                                                );
                                            }}
                                        >
                                            {isSecretFieldRevealed ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                                {fieldError && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {fieldError.message}
                                    </p>
                                )}
                                {credentialFieldRevealError !== undefined ? (
                                    <p className="text-red-600 text-sm mt-1">
                                        {credentialFieldRevealError}
                                    </p>
                                ) : null}
                            </div>
                        );
                    }
                )}
            </div>
        );
    };

    const isViewOnly = mode === 'view' && !isFormMode;
    const credentialTypeLockedForCreateMode: boolean =
        mode === 'create' && lockedCredentialType !== undefined;
    const shouldRenderSecretForm: boolean =
        (!isLoadingSecret || mode === 'create') &&
        (loadError === null || isUsingPublicFieldFallback || mode === 'create');

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent id="credential-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        <span className="flex items-center gap-2">
                            {getTitle()}
                            {secretFormHydrationSource !== null ? (
                                <CredentialStatusBadge
                                    status={secretFormHydrationSource.status}
                                    pendingReason={secretFormHydrationSource.pending_reason}
                                />
                            ) : null}
                        </span>
                    </DialogTitle>
                    <DialogDescription>{getDescription()}</DialogDescription>
                </DialogHeader>

                {loadError &&
                    !isUsingPublicFieldFallback &&
                    (mode === 'view' || mode === 'edit') && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {loadError instanceof Error
                                    ? loadError.message
                                    : 'Failed to load secret details'}
                            </AlertDescription>
                            <div className="mt-4 flex gap-2">
                                <Button type="button" onClick={() => void retryLoadSecret()}>
                                    Retry
                                </Button>
                                <Button type="button" variant="outline" onClick={handleCancel}>
                                    Close
                                </Button>
                            </div>
                        </Alert>
                    )}

                {isUsingPublicFieldFallback && (
                    <Alert>
                        <AlertDescription>
                            The stored credential cannot be decrypted. Public fields are shown;
                            replace every secret field before saving.
                        </AlertDescription>
                    </Alert>
                )}

                {isLoadingSecret && (mode === 'view' || mode === 'edit') && (
                    <div className="py-8 text-center text-muted-foreground">
                        Loading secret details...
                    </div>
                )}

                {shouldRenderSecretForm && (
                    <form
                        id="credential-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            runInBackground(handleSubmit(onSubmit)(e), (err) => {
                                log.error('Error submitting form', err);
                            });
                        }}
                        className="space-y-4"
                    >
                        {/* Generic websites derive the system from the selected website. */}
                        {mode !== 'view' && credentialTargetFamily !== 'generic_website' && (
                            <div>
                                <Label htmlFor="system_id">
                                    {resolvedCredentialSystemSelectLabel}
                                </Label>
                                <select
                                    id="system_id"
                                    {...register('system_id')}
                                    className="w-full border rounded px-3 py-2"
                                    disabled={isViewOnly || credentialSystemOptions.length <= 1}
                                >
                                    {credentialSystemOptions.length !== 1 && (
                                        <option value="">Select System ID...</option>
                                    )}
                                    {credentialSystemOptions.map((systemId) => (
                                        <option key={systemId} value={systemId}>
                                            {formatThirdPartySystemName(systemId)}
                                        </option>
                                    ))}
                                </select>
                                {errors.system_id && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.system_id.message}
                                    </p>
                                )}
                            </div>
                        )}

                        {mode === 'create' && credentialTargetFamily === 'generic_website' && (
                            <div>
                                <Label htmlFor="generic_website">Generic Website</Label>
                                <select
                                    id="generic_website"
                                    className="w-full border rounded px-3 py-2"
                                    disabled={isViewOnly || genericWebsiteOptions.length === 0}
                                    onChange={(event) => {
                                        setValue('account_id', event.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    value=""
                                >
                                    <option value="" disabled>
                                        Select generic website...
                                    </option>
                                    {genericWebsiteOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {genericWebsiteOptions.length === 0 && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        No generic websites are configured for this organization.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Account ID */}
                        <div>
                            <Label htmlFor="account_id">Mnemonic ID</Label>
                            <Input
                                id="account_id"
                                {...register('account_id')}
                                placeholder="Identifier for this credential set (e.g., Front Desk, Mobile Clinic, Online Store...)
"
                                disabled={isViewOnly}
                            />
                            {errors.account_id && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.account_id.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="credential_type">Credential Type</Label>
                            <select
                                id="credential_type"
                                {...register('credential_type')}
                                className="w-full border rounded px-3 py-2"
                                disabled={isViewOnly || credentialTypeLockedForCreateMode}
                            >
                                {credentialTypeOptions.map((credentialType) => (
                                    <option key={credentialType} value={credentialType}>
                                        {CredentialTypeDisplayName[credentialType]}
                                    </option>
                                ))}
                            </select>
                            {errors.credential_type && (
                                <p className="text-red-600 text-sm mt-1">
                                    {errors.credential_type.message}
                                </p>
                            )}
                        </div>

                        {selectedCredentialType !== CredentialType.IPX && (
                            <div>
                                <Label htmlFor="machine_credential_definition_uuid">
                                    Authentication Method
                                </Label>
                                <select
                                    id="machine_credential_definition_uuid"
                                    value={selectedIpxCredentialDefinitionUuid}
                                    onChange={handleMachineCredentialDefinitionChange}
                                    className="w-full border rounded px-3 py-2"
                                    disabled={
                                        isViewOnly ||
                                        isLoadingSecretCredentialDefinitions ||
                                        availableSecretCredentialDefinitions.length === 0
                                    }
                                >
                                    <option value="">
                                        {isLoadingSecretCredentialDefinitions
                                            ? 'Loading authentication methods'
                                            : 'Select authentication method'}
                                    </option>
                                    {availableSecretCredentialDefinitions.map((definition) => (
                                        <option
                                            key={definition.ipx_credential_definition_uuid}
                                            value={definition.ipx_credential_definition_uuid}
                                        >
                                            {getSecretCredentialDefinitionLabel(definition)}
                                        </option>
                                    ))}
                                </select>
                                {selectedSystemId === ThirdPartySystemId.CLOVER && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Clover credentials use OAuth for physical-terminal and
                                        card-not-present payments.
                                    </p>
                                )}
                            </div>
                        )}

                        {selectedCredentialType === CredentialType.IPX && (
                            <div>
                                <Label htmlFor="ipx_credential_definition_uuid">
                                    Credential Definition
                                </Label>
                                <select
                                    id="ipx_credential_definition_uuid"
                                    {...register('ipx_credential_definition_uuid')}
                                    className="w-full border rounded px-3 py-2"
                                    disabled={
                                        isViewOnly ||
                                        isLoadingIpxCredentialDefinitions ||
                                        availableIpxCredentialDefinitions.length === 0
                                    }
                                >
                                    <option value="">
                                        {isLoadingIpxCredentialDefinitions
                                            ? 'Loading credential definitions'
                                            : 'Select credential definition'}
                                    </option>
                                    {availableIpxCredentialDefinitions.map((definition) => (
                                        <option
                                            key={definition.ipx_credential_definition_uuid}
                                            value={definition.ipx_credential_definition_uuid}
                                        >
                                            {getIpxCredentialDefinitionLabel(definition)}
                                        </option>
                                    ))}
                                </select>
                                {availableIpxCredentialDefinitions.length === 0 &&
                                    !isLoadingIpxCredentialDefinitions && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            No credential definitions are available for this
                                            organization.
                                        </p>
                                    )}
                                {ipxCredentialDefinitionsError && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {ipxCredentialDefinitionsError instanceof Error
                                            ? ipxCredentialDefinitionsError.message
                                            : 'Failed to load credential definitions'}
                                    </p>
                                )}
                                {errors.ipx_credential_definition_uuid && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {errors.ipx_credential_definition_uuid.message}
                                    </p>
                                )}
                            </div>
                        )}

                        {selectedCredentialType === CredentialType.IPX &&
                            selectedIpxCredentialDefinition !== null && (
                                <div>
                                    <Label htmlFor="credential_owner_scope">Credential Scope</Label>
                                    <select
                                        id="credential_owner_scope"
                                        {...register('credential_owner_scope')}
                                        className="w-full border rounded px-3 py-2"
                                        disabled={
                                            isViewOnly ||
                                            selectedIpxCredentialDefinitionOwnerScope !== null
                                        }
                                    >
                                        <option value="">Select credential scope</option>
                                        <option value={CredentialOwnerScope.ROLE_ASSIGNMENT}>
                                            {getCredentialOwnerScopeDisplayName(
                                                CredentialOwnerScope.ROLE_ASSIGNMENT
                                            )}
                                        </option>
                                        <option value={CredentialOwnerScope.ORGANIZATION}>
                                            {getCredentialOwnerScopeDisplayName(
                                                CredentialOwnerScope.ORGANIZATION
                                            )}
                                        </option>
                                    </select>
                                    {selectedIpxCredentialDefinitionOwnerScope !== null && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Scope is fixed by the credential definition.
                                        </p>
                                    )}
                                    {errors.credential_owner_scope && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {errors.credential_owner_scope.message}
                                        </p>
                                    )}
                                </div>
                            )}

                        {renderCredentialFields()}

                        {/* Settings */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="applies_to_children"
                                    checked={appliesToChildren}
                                    onCheckedChange={(checked) =>
                                        setValue('applies_to_children', checked === true)
                                    }
                                    disabled={isViewOnly}
                                />
                                <Label htmlFor="applies_to_children" className="font-normal">
                                    Apply to child organizations
                                </Label>
                            </div>
                            <p className="text-sm text-muted-foreground ml-6">
                                Subsidiary merchants can inherit and use this secret
                            </p>

                            {mode === 'edit' && (
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="status"
                                        checked={isActive}
                                        onCheckedChange={(checked) =>
                                            setValue(
                                                'status',
                                                checked === true ? Status.ACTIVE : Status.DISABLED
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                    <Label htmlFor="status" className="font-normal">
                                        Active
                                    </Label>
                                </div>
                            )}
                        </div>

                        {saveMutation.error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {saveMutation.error instanceof Error
                                        ? saveMutation.error.message
                                        : 'Failed to save secret'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {duplicateActiveIpxCredentialMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {duplicateActiveIpxCredentialMessage}
                                </AlertDescription>
                            </Alert>
                        )}
                        {postSaveErrorMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>{postSaveErrorMessage}</AlertDescription>
                            </Alert>
                        )}
                        {credentialTestResult !== null && (
                            <Alert
                                role="status"
                                variant={
                                    credentialTestResult.status === CredentialTestStatus.NOT_READY
                                        ? 'destructive'
                                        : 'default'
                                }
                            >
                                <AlertDescription>
                                    {credentialTestResult.status === CredentialTestStatus.READY &&
                                        'Credential is ready.'}
                                    {credentialTestResult.status ===
                                        CredentialTestStatus.NOT_APPLICABLE &&
                                        'Credential testing is not applicable to this authentication method.'}
                                    {credentialTestResult.status ===
                                        CredentialTestStatus.NOT_READY && (
                                        <ul className="list-disc pl-5">
                                            {credentialTestResult.failures.map((failure) => (
                                                <li key={`${failure.reason}:${failure.message}`}>
                                                    {failure.message}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {credentialTestResult.status ===
                                        CredentialTestStatus.UNVERIFIED && (
                                        <ul className="list-disc pl-5">
                                            {credentialTestResult.findings.map((finding) => (
                                                <li key={`${finding.reason}:${finding.message}`}>
                                                    {finding.message}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}
                        {credentialTestErrorMessage !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>{credentialTestErrorMessage}</AlertDescription>
                            </Alert>
                        )}

                        {credentialTestPrerequisiteMessage !== null && (
                            <p
                                id="credential-test-prerequisite"
                                role="status"
                                className="text-sm text-muted-foreground"
                            >
                                {credentialTestPrerequisiteMessage}
                            </p>
                        )}
                        <DialogFooter>
                            {missingCredentialFields.length > 0 && !isViewOnly && (
                                <div className="text-sm text-muted-foreground mr-auto pr-4">
                                    Missing fields:{' '}
                                    <span className="font-medium">
                                        {missingCredentialFields.join(', ')}
                                    </span>
                                    . Save now to keep this as an inactive draft.
                                </div>
                            )}
                            {isViewOnly ? (
                                <>
                                    <Button
                                        id="credential-test"
                                        aria-describedby={
                                            credentialTestPrerequisiteMessage === null
                                                ? undefined
                                                : 'credential-test-prerequisite'
                                        }
                                        type="button"
                                        variant="outline"
                                        onClick={() => void testCredential()}
                                        disabled={!canTestCredential || isCredentialTestPending}
                                    >
                                        {isCredentialTestPending ? 'Testing...' : 'Test Credential'}
                                    </Button>
                                    <Button type="button" variant="outline" onClick={handleCancel}>
                                        Close
                                    </Button>
                                    <Button type="button" onClick={handleSwitchToEdit}>
                                        Edit
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        id="credential-test"
                                        aria-describedby={
                                            credentialTestPrerequisiteMessage === null
                                                ? undefined
                                                : 'credential-test-prerequisite'
                                        }
                                        type="button"
                                        variant="outline"
                                        onClick={() => void testCredential()}
                                        disabled={!canTestCredential || isCredentialTestPending}
                                    >
                                        {isCredentialTestPending ? 'Testing...' : 'Test Credential'}
                                    </Button>
                                    <Button
                                        id="credential-cancel"
                                        type="button"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={saveMutation.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        id="credential-save"
                                        type="submit"
                                        disabled={
                                            saveMutation.isPending ||
                                            duplicateActiveIpxCredentialMessage !== null ||
                                            isSystemIdSelectionInvalid
                                        }
                                    >
                                        {saveMutation.isPending ? 'Saving...' : 'Save'}
                                    </Button>
                                </>
                            )}
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
